import { useState, type FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api';
import { useAuth } from '../auth/useAuth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useLoginFlow } from './useLoginFlow';

export function CredentialsForm() {
  const { tokens, authenticate } = useAuth();
  const { dispatch } = useLoginFlow();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already authenticated — redirect to dashboard.
  useEffect(() => {
    if (tokens) navigate('/', { replace: true });
  }, [tokens, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    dispatch({ type: 'SUBMIT_CREDENTIALS' });
    setLoading(true);

    try {
      const result = await authApi.login({ email, password });

      if (result.kind === 'mfa_enrollment_required') {
        dispatch({
          type: 'MFA_ENROLLMENT',
          challengeToken: result.challengeToken ?? '',
          qrCodeUri: result.qrCodeUri ?? '',
          manualEntryKey: result.manualEntryKey ?? '',
        });
        void navigate('/login/mfa-enroll');
        return;
      }

      if (result.kind === 'mfa_required') {
        dispatch({ type: 'MFA_CHALLENGE', challengeToken: result.challengeToken ?? '' });
        void navigate('/login/mfa-challenge');
        return;
      }

      // Narrowed on `kind === 'token'` rather than by elimination. The spec marks
      // the discriminator nullable, so a `!== 'mfa_required'` test does not exclude
      // that member and the union stays un-narrowed. Asserting the positive case is
      // correct either way, and does not depend on the spec being tightened.
      if (result.kind !== 'token') {
        const msg = 'Sign in failed. Try again.';
        dispatch({ type: 'ERROR', message: msg });
        setError(msg);
        return;
      }

      const { accessToken, refreshToken, expiresAt, user } = result.tokens ?? {};
      if (!accessToken || !refreshToken || !expiresAt) {
        const msg = 'Sign in failed. Try again.';
        dispatch({ type: 'ERROR', message: msg });
        setError(msg);
        return;
      }
      dispatch({ type: 'AUTHENTICATED' });
      authenticate({ accessToken, refreshToken, expiresAt }, user ?? null);
      void navigate('/', { replace: true });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // 401 = bad credentials; 403 = valid credentials but not a privileged user.
      const msg =
        status === 401 || status === 403
          ? 'This account does not have access to the admin panel.'
          : 'Sign in failed. Try again.';
      dispatch({ type: 'ERROR', message: msg });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Input
        id="email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        required
      />

      <Input
        id="password"
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        required
      />

      {error && (
        <div
          style={{
            background: 'var(--badge-rejected-bg)',
            border: '1px solid var(--badge-rejected-text)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            fontSize: 'var(--text-sm)',
            color: 'var(--badge-rejected-text)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" variant="primary" size="lg" loading={loading} style={{ width: '100%', marginTop: 4 }}>
        {loading ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}
